import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Card,
  CardContent,
  Paper,
  Stack,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { fromZonedTime, format, toZonedTime } from "date-fns-tz";

function CornContractPrices() {
  const token = localStorage.getItem("token");
  const [localBasisPrice, setLocalBasisPrice] = useState("");
  const [futuresPrice, setFuturesPrice] = useState("");
  const [displayDate, setDisplayDate] = useState("");
  const [date, setDate] = useState("");
  const [submissionMessage, setSubmissionMessage] = useState("");
  const [contractPriceTableData, setContractPriceTableData] = useState([]);

  const fetchContractPrices = () => {
    axios
      .get("/api/cornGetContractPrices", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          const sortedData = response.data.sort(
            (a, b) => new Date(b.date) - new Date(a.date), // DESC order
          );
          setContractPriceTableData(sortedData);
        } else {
          console.error("Failed to fetch data from the backend");
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const saveCornContractPrices = async () => {
    try {
      const formDataToSubmit = {
        date: date,
        localBasisPrice: parseFloat(localBasisPrice),
        futuresPrice: parseFloat(futuresPrice),
      };

      const response = await axios.post(
        "/api/cornAddContractPrices",
        formDataToSubmit,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status >= 200 && response.status < 300) {
        console.log("Data sent to the backend successfully");
        setLocalBasisPrice("");
        setFuturesPrice("");
        setDisplayDate("");
        setSubmissionMessage("✅ Prices submitted successfully!");
        fetchContractPrices();
      } else {
        console.error("Failed to send data to the backend");
        setSubmissionMessage("❌ Failed to submit prices.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmissionMessage("❌ Error submitting prices.");
    }
  };

  const handleDelete = async (date) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this contract price entry?",
      )
    ) {
      return;
    }

    try {
      const response = await axios.delete(
        `/api/cornDeleteContractPrice/${encodeURIComponent(date)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200) {
        setSubmissionMessage("✅ Prices deleted successfully!");
        fetchContractPrices();
      } else {
        setSubmissionMessage("❌ Failed to delete prices.");
      }
    } catch (error) {
      console.error("Error deleting contract prices:", error);
      setSubmissionMessage("❌ Error deleting prices.");
    }
  };

  useEffect(() => {
    fetchContractPrices();
  }, []);

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;

    const timeZone = "America/Chicago";
    const localMidnight = new Date(`${selectedDate}T00:00:00`);
    const utcDate = fromZonedTime(localMidnight, timeZone);

    setDisplayDate(selectedDate); // shown in textfield
    setDate(utcDate.toISOString()); // sent to backend
  };

  return (
    <div>
      <h2>Contract Prices</h2>
      {submissionMessage && (
        <Typography
          variant="body1"
          style={{
            color: submissionMessage.includes("successfully") ? "green" : "red",
            marginBottom: "10px",
          }}
        >
          {submissionMessage}
        </Typography>
      )}

      <div style={{ display: "flex", alignItems: "flex-start", gap: "20px" }}>
        {/* Left: Form */}
        <div style={{ minWidth: "300px", flex: 1 }}>
          <Stack spacing={2}>
            <TextField
              label="Date"
              variant="outlined"
              fullWidth
              type="date"
              value={displayDate}
              onChange={handleDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />

            <TextField
              label="Local Basis Price ($/bushel)"
              variant="outlined"
              fullWidth
              type="number"
              step="0.01"
              value={localBasisPrice}
              onChange={(e) => setLocalBasisPrice(e.target.value)}
              required
            />

            <TextField
              label="Futures Price ($/bushel)"
              variant="outlined"
              fullWidth
              type="number"
              step="0.01"
              value={futuresPrice}
              onChange={(e) => setFuturesPrice(e.target.value)}
              required
            />

            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={saveCornContractPrices}
            >
              Submit Contract Prices
            </Button>
          </Stack>
        </div>

        {/* Right: Table */}
        <TableContainer
          component={Paper}
          style={{
            flex: 2,
            maxHeight: "350px",
            overflowY: "auto",
            maxWidth: "1000px",
            marginRight: "50px",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#002657" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Date
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Local Basis Price ($/bu)
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Futures Price ($/bu)
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Flat Contract Price ($/bu)
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contractPriceTableData.map((row, index) => {
                const localBasis = row.local_basis_price
                  ? parseFloat(row.local_basis_price)
                  : 0;
                const futures = row.futures_price
                  ? parseFloat(row.futures_price)
                  : 0;
                const flatPrice = localBasis + futures;

                // Parse date without timezone conversion
                const dateStr = row.date.split("T")[0];
                const [year, month, day] = dateStr.split("-");
                const displayDate = new Date(
                  year,
                  month - 1,
                  day,
                ).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                });

                return (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                    }}
                  >
                    <TableCell>{displayDate}</TableCell>
                    <TableCell align="center">
                      {row.local_basis_price
                        ? row.local_basis_price.toFixed(2)
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {row.futures_price ? row.futures_price.toFixed(2) : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {flatPrice > 0 ? flatPrice.toFixed(2) : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(row.date)}
                        size="small"
                        title="Delete entry"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
}

export default CornContractPrices;
